import { action } from "typesafe-actions";
import {
  ICategory,
  ICategoryState,
  IMasterProduct,
  IProductState,
} from "./masterCategories.types";
import { ThunkAction } from "redux-thunk";
import { buildTableQueryString, IStoreState, ITableQueryParams } from "..";
import { AnyAction } from "redux";
import { api } from "../../api/api";
import { showMessage } from "../messages/messagesActions";
import { IDate } from "../../components/Table/hooks/useDateFilter";

export const FETCH_CATEGORY_LIST_PROGRESS = "FETCH_CATEGORY_LIST_PROGRESS";
export const FETCH_CATEGORY_LIST_SUCCESS = "FETCH_CATEGORY_LIST_SUCCESS";
export const FETCH_CATEGORY_LIST_FAILED = "FETCH_CATEGORY_LIST_FAILED";

export const fetchCategoryListProgress = () =>
  action(FETCH_CATEGORY_LIST_PROGRESS);
export const fetchCategoryListSuccess = (
  data: ICategoryState["list"],
  totalRecords: number,
) => action(FETCH_CATEGORY_LIST_SUCCESS, { data, totalRecords });
export const fetchCategoryListFailed = () => action(FETCH_CATEGORY_LIST_FAILED);

export const fetchCategoryListAsync =
  (
    pageNumber: number,
    rowsInPerPage: number,
    status: string,
    date: IDate,
    searchValue: string,
    searchType: string[],
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchCategoryListProgress());
      let url = "";
      if (status !== "-1") {
        url = "&status=" + status;
      }
      let finalUrl = `/category/get-category?pageNo=${pageNumber}&itemPerPage=${rowsInPerPage}&from_date=${date.fromDate}&to_date=${date.toDate}${url}`;
      if (searchType.length > 0 && searchValue) {
        finalUrl = `/category/get-category?pageNo=${pageNumber}&itemPerPage=${rowsInPerPage}&columns=${[
          searchType,
        ]}&value=${searchValue}`;
      }

      const res = await api.get(finalUrl);
      const data: ICategoryState["list"] = res.data.data;

      const totalRecords = res.data.totalRecords;

      dispatch(fetchCategoryListSuccess(data, totalRecords));
    } catch (err: any) {
      dispatch(fetchCategoryListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Failed to fetch categories",
          displayAs: "snackbar",
        }),
      );
    }
  };

export const upsertCategoryAsync =
  (
    data: ICategory,
    onCallback: (isSuccess: boolean, category?: ICategory) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const { category_id, create_ts, insert_ts, ...rest } = data;
      const res = await api.post("/category/upsert_category", rest);
      let message = "Category saved successfully!";
      onCallback(true, res.data.data);
      dispatch(
        showMessage({
          type: "success",
          displayAs: "snackbar",
          message: message,
        }),
      );
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Failed to save category",
          displayAs: "snackbar",
        }),
      );
    }
  };

export const FETCH_PRODUCT_LIST_PROGRESS = "FETCH_PRODUCT_LIST_PROGRESS";
export const FETCH_PRODUCT_LIST_SUCCESS = "FETCH_PRODUCT_LIST_SUCCESS";
export const FETCH_PRODUCT_LIST_FAILED = "FETCH_PRODUCT_LIST_FAILED";

export const fetchProductListProgress = () =>
  action(FETCH_PRODUCT_LIST_PROGRESS);
export const fetchProductListSuccess = (
  data: IProductState["list"],
  totalRecords: number,
) => action(FETCH_PRODUCT_LIST_SUCCESS, { data, totalRecords });
export const fetchProductListFailed = () => action(FETCH_PRODUCT_LIST_FAILED);

export const fetchProductListAsync =
  (params: ITableQueryParams): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const queryParams = buildTableQueryString(params);
      dispatch(fetchProductListProgress());
      const res = await api.get(`/product/get-product${queryParams}`);
      const data: IProductState["list"] = res.data.data;

      const totalRecords = res.data.totalRecords;

      dispatch(fetchProductListSuccess(data, totalRecords));
    } catch (err: any) {
      dispatch(fetchProductListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Failed to fetch products",
          displayAs: "snackbar",
        }),
      );
    }
  };

export const upsertProductAsync =
  (
    data: IMasterProduct,
    onCallback: (isSuccess: boolean, product?: IMasterProduct) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const {
        product_id,
        create_ts,
        insert_ts,
        unique_id,
        product_code_prefix,
        running_no,
        combined_product_code,
        ...rest
      } = data;
      const payload = { ...rest, product_uuid: data.product_uuid || null };
      const res = await api.post("/product/upsert_product", payload);
      let message = "Product saved successfully!";
      onCallback(true, res.data.data);
      dispatch(
        showMessage({
          type: "success",
          displayAs: "snackbar",
          message: message,
        }),
      );
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Failed to save product",
          displayAs: "snackbar",
        }),
      );
    }
  };

export const CLEAR_CATEGORY_STATE = "CLEAR_CATEGORY_STATE";
export const clearCategoryState = () => action(CLEAR_CATEGORY_STATE);

export const CLEAR_PRODUCT_STATE = "CLEAR_PRODUCT_STATE";
export const clearProductState = () => action(CLEAR_PRODUCT_STATE);

export const CLEAR_MASTER_CATEGORIES_STATE = "CLEAR_MASTER_CATEGORIES_STATE";
export const clearMasterCategoriesState = () =>
  action(CLEAR_MASTER_CATEGORIES_STATE);
